import { useEffect, useMemo, useState, type DragEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, BookOpen, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { ProgrammeModel } from '@/models/programme.model'
import { CourseModel } from '@/models/course.model'
import { ProgrammeCurriculumModel } from '@/models/programme-curriculum.model'
import { programmesService } from '@/services/programmesService'
import { coursesService } from '@/services/coursesService'
import { programmeCurriculumService } from '@/services/programmeCurriculumService'
import {
  useProgrammeCurriculums,
  useCreateProgrammeCurriculum,
  useUpdateProgrammeCurriculum,
  useDeleteProgrammeCurriculum,
  programmeCurriculumKeys,
} from '@/hooks/useProgrammeCurriculums'
import {
  ProgrammeCurriculumForm,
  ProgrammeCurriculumFormValues,
} from '@/components/app/programmes/ProgrammeCurriculumForm'
import { ConfirmDeleteDialog } from '@/components/app/common/ConfirmDeleteDialog'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { paths } from '@/routes/path'
import { cn } from '@/utils/cn'

type BannerState = { type: 'success' | 'error'; message: string } | null

export function ProgrammeCurriculumsPage() {
  const { programmeId } = useParams<{ programmeId: string }>()
  const navigate = useNavigate()
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [formDefaults, setFormDefaults] = useState<Partial<ProgrammeCurriculumFormValues> | undefined>()
  const [editingCurriculum, setEditingCurriculum] = useState<ProgrammeCurriculumModel | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [curriculumToDelete, setCurriculumToDelete] = useState<ProgrammeCurriculumModel | null>(null)
  const [banner, setBanner] = useState<BannerState>(null)
  const [orderedCurriculums, setOrderedCurriculums] = useState<ProgrammeCurriculumModel[]>([])
  const [draggedCurriculumId, setDraggedCurriculumId] = useState<string | null>(null)
  const [dropTargetCurriculumId, setDropTargetCurriculumId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const queryClient = useQueryClient()

  const programmeQuery = useQuery({
    queryKey: ['programme', programmeId],
    queryFn: async (): Promise<ProgrammeModel> => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmesService.getProgramme(programmeId, { requireAuth: true })
    },
    enabled: Boolean(programmeId),
    staleTime: 5 * 60 * 1000,
  })

  const coursesQuery = useQuery({
    queryKey: ['allCourses'],
    queryFn: async (): Promise<CourseModel[]> => {
      const response = await coursesService.getAllCourses({ limit: 200 })
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const curriculumsQuery = useProgrammeCurriculums(programmeId)
  const curriculums = useMemo(() => curriculumsQuery.data ?? [], [curriculumsQuery.data])
  const isCurriculumsLoading = curriculumsQuery.isLoading
  const curriculumsError = curriculumsQuery.error as Error | null

  const createMutation = useCreateProgrammeCurriculum(programmeId)
  const updateMutation = useUpdateProgrammeCurriculum(programmeId, editingCurriculum?.id)
  const deleteMutation = useDeleteProgrammeCurriculum(programmeId)
  const reorderMutation = useMutation({
    mutationFn: async (
      payload: Array<{
        id: string
        courseId: string
        order: number
        year: number | null
        semester: number | null
        required: boolean
      }>,
    ) => {
      if (!programmeId) throw new Error('Programme ID is required')
      await Promise.all(
        payload.map((item) =>
          programmeCurriculumService.updateCurriculum(programmeId, item.id, {
            courseId: item.courseId,
            year: item.year,
            semester: item.semester,
            required: item.required,
            order: item.order,
          }),
        ),
      )
    },
    onSuccess: () => {
      if (!programmeId) return
      setBanner({ type: 'success', message: 'Curriculum order updated.' })
      queryClient.invalidateQueries({ queryKey: programmeCurriculumKeys.list(programmeId) })
    },
    onError: (error: Error) => {
      setBanner({
        type: 'error',
        message: error.message || 'Unable to update curriculum order.',
      })
    },
  })

  const isProgrammeLoading = programmeQuery.isLoading
  const programmeError = programmeQuery.error as Error | null
  const programme = programmeQuery.data

  const isCoursesLoading = coursesQuery.isLoading
  const coursesError = coursesQuery.error as Error | null
  const courses = useMemo(() => coursesQuery.data ?? [], [coursesQuery.data])

  useEffect(() => {
    const sorted = curriculums
      .slice()
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) {
          return orderA - orderB
        }
        const createdA = a.createdAt ?? ''
        const createdB = b.createdAt ?? ''
        return createdA.localeCompare(createdB)
      })
    setOrderedCurriculums(sorted)
  }, [curriculums])

  useEffect(() => {
    if (!banner) return
    const timeout = setTimeout(() => setBanner(null), 4000)
    return () => clearTimeout(timeout)
  }, [banner])

  const courseOptions = useMemo(() => {
    const options = new Map<string, { id: string; title: string }>()
    courses.forEach((course) => {
      options.set(course.id, { id: course.id, title: course.title })
    })

    if (editingCurriculum && editingCurriculum.courseId) {
      const existing = options.get(editingCurriculum.courseId)
      if (!existing) {
        options.set(editingCurriculum.courseId, {
          id: editingCurriculum.courseId,
          title: editingCurriculum.course?.title ?? editingCurriculum.courseId,
        })
      }
    }

    return Array.from(options.values()).sort((a, b) => a.title.localeCompare(b.title))
  }, [courses, editingCurriculum])

  const curriculumsForDisplay =
    orderedCurriculums.length > 0 ? orderedCurriculums : curriculums
  const curriculumCount = curriculums.length

  const computeNextOrder = (items: ProgrammeCurriculumModel[]) => {
    if (!items.length) return 1
    return items.reduce((max, item) => Math.max(max, item.order ?? 0), 0) + 1
  }

  const normalizeNumeric = (value?: string) => {
    if (!value) return null
    const trimmed = value.trim()
    if (!trimmed) return null
    return Number(trimmed)
  }

  const mapFormValuesToPayload = (
    values: ProgrammeCurriculumFormValues,
    order: number | null,
  ) => ({
    courseId: values.courseId,
    year: normalizeNumeric(values.year),
    semester: normalizeNumeric(values.semester),
    required: values.required,
    order: order ?? null,
  })

  const applyReorder = (sourceId: string, targetId: string, position: 'before' | 'after') => {
    setOrderedCurriculums((prev) => {
      const previousState = [...prev]
      const updated = [...prev]

      if (sourceId === targetId) {
        return prev
      }

      const fromIndex = updated.findIndex((item) => item.id === sourceId)
      if (fromIndex === -1) {
        return prev
      }

      const [movedItem] = updated.splice(fromIndex, 1)

      let toIndex = updated.findIndex((item) => item.id === targetId)
      if (toIndex === -1) {
        return prev
      }

      if (position === 'after') {
        toIndex += 1
      }

      updated.splice(toIndex, 0, movedItem)

      const payload = updated.map((item, index) => ({
        id: item.id,
        courseId: item.courseId,
        order: index + 1,
        year: item.year ?? null,
        semester: item.semester ?? null,
        required: item.required,
      }))

      reorderMutation.mutate(payload, {
        onError: () => {
          setOrderedCurriculums(previousState)
        },
      })

      return updated.map((item, index) => ({ ...item, order: index + 1 })) as ProgrammeCurriculumModel[]
    })
  }

  const handleDragStart = (event: DragEvent<HTMLButtonElement>, curriculumId: string) => {
    if (reorderMutation.isPending) {
      event.preventDefault()
      return
    }

    event.stopPropagation()
    setDraggedCurriculumId(curriculumId)
    setIsDragging(true)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', curriculumId)
  }

  const handleDragEnter = (event: DragEvent<HTMLTableRowElement>, curriculumId: string) => {
    event.preventDefault()
    if (!draggedCurriculumId || draggedCurriculumId === curriculumId) return
    setDropTargetCurriculumId(curriculumId)
  }

  const handleDragOver = (event: DragEvent<HTMLTableRowElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (event: DragEvent<HTMLTableRowElement>, targetId: string) => {
    event.preventDefault()
    const sourceId = event.dataTransfer.getData('text/plain') || draggedCurriculumId
    setDropTargetCurriculumId(null)

    if (!sourceId || sourceId === targetId) {
      setIsDragging(false)
      setDraggedCurriculumId(null)
      return
    }

    const { top, height } = event.currentTarget.getBoundingClientRect()
    const position: 'before' | 'after' =
      event.clientY > top + height / 2 ? 'after' : 'before'

    applyReorder(sourceId, targetId, position)
    setIsDragging(false)
    setDraggedCurriculumId(null)
  }

  const handleDragEnd = () => {
    setDraggedCurriculumId(null)
    setDropTargetCurriculumId(null)
    setIsDragging(false)
  }

  const handleDragLeave = (event: DragEvent<HTMLTableRowElement>, curriculumId: string) => {
    const related = event.relatedTarget as Node | null
    if (!related || !event.currentTarget.contains(related)) {
      if (dropTargetCurriculumId === curriculumId) {
        setDropTargetCurriculumId(null)
      }
    }
  }

  if (!programmeId) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-600">
        Programme identifier is required to manage curriculum entries.
        <Button className="mt-4" variant="outline" onClick={() => navigate(paths.app.programmes.root.absolute)}>
          Back to programmes
        </Button>
      </div>
    )
  }

  if (isProgrammeLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">
        Loading programme information…
      </div>
    )
  }

  if (programmeError || !programme) {
    return (
      <Card className="mx-auto max-w-3xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">Unable to load programme</h1>
          <p className="text-secondary-600">
            {programmeError?.message ?? 'This programme could not be found.'}
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => navigate(paths.app.programmes.root.absolute)}>
              Back to programmes
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const openCreateForm = () => {
    setFormMode('create')
    setEditingCurriculum(null)
    setFormDefaults(undefined)
    setFormOpen(true)
  }

  const openEditForm = (entry: ProgrammeCurriculumModel) => {
    setFormMode('edit')
    setEditingCurriculum(entry)
    setFormDefaults({
      courseId: entry.courseId,
      year: entry.year !== undefined && entry.year !== null ? String(entry.year) : '',
      semester: entry.semester !== undefined && entry.semester !== null ? String(entry.semester) : '',
      required: entry.required,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingCurriculum(null)
  }

  const handleFormSubmit = async (values: ProgrammeCurriculumFormValues) => {
    const baseOrder =
      formMode === 'create'
        ? computeNextOrder(curriculumsForDisplay)
        : editingCurriculum?.order ?? null
    const payload = mapFormValuesToPayload(values, baseOrder)
    try {
      if (formMode === 'create') {
        await createMutation.mutateAsync(payload)
        setBanner({ type: 'success', message: 'Curriculum entry added successfully.' })
      } else if (editingCurriculum?.id) {
        await updateMutation.mutateAsync(payload)
        setBanner({ type: 'success', message: 'Curriculum entry updated successfully.' })
      }
      closeForm()
    } catch (error) {
      setBanner({
        type: 'error',
        message: (error as Error).message || 'Unable to save curriculum entry.',
      })
    }
  }

  const handleDeleteCurriculum = async () => {
    if (!curriculumToDelete) return
    try {
      await deleteMutation.mutateAsync(curriculumToDelete.id)
      setBanner({ type: 'success', message: 'Curriculum entry deleted successfully.' })
      setDeleteDialogOpen(false)
      setCurriculumToDelete(null)
    } catch (error) {
      setBanner({
        type: 'error',
        message: (error as Error).message || 'Unable to delete curriculum entry.',
      })
    }
  }

  const disableCreateAction = isCoursesLoading || courses.length === 0
  const isFormSubmitting =
    formMode === 'create' ? createMutation.isPending : updateMutation.isPending

  const deleteEntityName = curriculumToDelete
    ? curriculumToDelete.course?.title ??
    courses.find((course) => course.id === curriculumToDelete.courseId)?.title ??
    curriculumToDelete.courseId
    : undefined

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <Link
              to={paths.app.programmes.detail.build(programme.id ?? '')}
              className="inline-flex items-center gap-2 hover:text-secondary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to programme
            </Link>
            <span className="hidden sm:inline text-secondary-300">/</span>
            <span className="inline-flex items-center gap-1 text-secondary-600">
              <BookOpen className="h-4 w-4 text-[#7b2a40]" />
              Curriculum
            </span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">{programme.title}</h1>
            <p className="text-secondary-600">
              Manage the courses that make up this programme’s curriculum.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            onClick={openCreateForm}
            disabled={disableCreateAction}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Curriculum Entry
          </Button>
          <Button variant="outline" onClick={() => navigate(paths.app.programmes.edit.build(programme.id ?? ''))}>
            Edit Programme
          </Button>
        </div>
      </header>

      {banner && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${banner.type === 'success'
            ? 'border-green-200 bg-green-50 text-green-700'
            : 'border-red-200 bg-red-50 text-red-600'
            }`}
          role="status"
        >
          {banner.message}
        </div>
      )}

      {coursesError && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {coursesError.message || 'Unable to load course options. Some actions may be disabled.'}
        </div>
      )}

      {disableCreateAction && !isCoursesLoading && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Add at least one course to the catalogue to enable curriculum mappings.
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Curriculum Overview</CardTitle>
            <p className="text-sm text-secondary-500">
              {isCurriculumsLoading
                ? 'Loading curriculum entries…'
                : curriculumCount === 0
                  ? 'No curriculum entries have been added yet.'
                  : `Showing ${curriculumCount} curriculum entr${curriculumCount === 1 ? 'y' : 'ies'}.`}
            </p>
          </div>
          {curriculumCount > 0 && (
            <Badge variant="outline" className="bg-secondary-50 text-secondary-700">
              {curriculumCount} total
            </Badge>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {curriculumsError && (
            <div className="px-6 py-4 text-sm text-red-600">
              {curriculumsError.message || 'Unable to load programme curriculums.'}
            </div>
          )}

          {!isCurriculumsLoading && curriculumCount === 0 && !curriculumsError && (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-secondary-600">
              <BookOpen className="h-12 w-12 text-secondary-300" />
              <div>
                <p className="text-lg font-semibold text-secondary-800">No curriculum entries yet</p>
                <p>Add the first course mapping to start shaping this programme.</p>
              </div>
              <Button onClick={openCreateForm} disabled={disableCreateAction}>
                <Plus className="mr-2 h-4 w-4" />
                Add Curriculum Entry
              </Button>
            </div>
          )}

          {curriculumCount > 0 && (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="w-12 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      <span className="sr-only">Reorder</span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      Semester
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      Required
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-secondary-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 bg-white">
                  {curriculumsForDisplay.map((entry) => {
                    const isDropTarget =
                      dropTargetCurriculumId === entry.id && draggedCurriculumId !== entry.id
                    const isDraggingRow = draggedCurriculumId === entry.id && isDragging
                    const courseTitle =
                      entry.course?.title ??
                      courses.find((course) => course.id === entry.courseId)?.title ??
                      entry.courseId
                    const courseCode =
                      entry.course?.code ??
                      courses.find((course) => course.id === entry.courseId)?.code ??
                      entry.courseId
                    const courseDetailPath = paths.app.courses.detail.build(entry.courseId)
                    const rowClassName = cn(
                      'transition-colors duration-150',
                      'hover:bg-secondary-50/60',
                      isDropTarget ? 'bg-[#7b2a40]/5 ring-1 ring-[#7b2a40]/30' : '',
                      isDraggingRow ? 'opacity-70' : '',
                      reorderMutation.isPending ? 'cursor-wait' : '',
                    )

                    return (
                      <tr
                        key={entry.id}
                        className={rowClassName}
                        onDragEnter={(event) => handleDragEnter(event, entry.id)}
                        onDragOver={handleDragOver}
                        onDragLeave={(event) => handleDragLeave(event, entry.id)}
                        onDrop={(event) => handleDrop(event, entry.id)}
                      >
                        <td className="px-6 py-4 align-middle">
                          <button
                            type="button"
                            className="flex h-8 w-8 items-center justify-center rounded-md text-secondary-400 hover:text-secondary-700 focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
                            draggable={!reorderMutation.isPending}
                            onDragStart={(event) => handleDragStart(event, entry.id)}
                            onDragEnd={handleDragEnd}
                            aria-label="Reorder curriculum entry"
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <Link to={courseDetailPath} className="group flex flex-col">
                            <span className="font-medium text-secondary-900 transition group-hover:text-primary-600 group-hover:underline">
                              {courseTitle}
                            </span>
                            <span className="text-xs text-secondary-500 transition group-hover:text-primary-500">
                              {courseCode}
                            </span>
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-secondary-700">
                          {entry.year ?? '—'}
                        </td>
                        <td className="px-6 py-4 text-secondary-700">
                          {entry.semester ?? '—'}
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={entry.required ? 'bg-green-100 text-green-700' : 'bg-secondary-100 text-secondary-700'}>
                            {entry.required ? 'Required' : 'Optional'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-secondary-600 hover:text-secondary-900"
                              onClick={() => openEditForm(entry)}
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Edit curriculum entry</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => {
                                setCurriculumToDelete(entry)
                                setDeleteDialogOpen(true)
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete curriculum entry</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={formMode === 'create' ? 'Add Curriculum Entry' : 'Edit Curriculum Entry'}
        size="lg"
      >
        <ProgrammeCurriculumForm
          mode={formMode}
          courses={courseOptions}
          defaultValues={formDefaults}
          submitting={isFormSubmitting}
          onSubmit={handleFormSubmit}
          onCancel={closeForm}
        />
      </Modal>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        entityLabel="Curriculum Entry"
        entityName={deleteEntityName}
        onClose={() => {
          setDeleteDialogOpen(false)
          setCurriculumToDelete(null)
        }}
        onConfirm={handleDeleteCurriculum}
        loading={deleteMutation.isPending}
      />
    </div>
  )
}

export default ProgrammeCurriculumsPage
